import clsx from 'clsx';
import { CSSProperties } from 'react';
import * as React from 'react';

import styles from './TextButton.module.scss';
import { BrightTooltip } from 'components/tooltips/BrightTooltip';
import { TooltipProps } from '@mui/material';

export enum TextButtonWidth {
  Content = 'content',
  Fullwidth = 'fullwidth',
}

export enum TextButtonColor {
  Default = 'default',
  Primary = 'primary',
  Destroy = 'destroy',
  GreyPlain = 'grey-plain',
}

export enum TextButtonOrder {
  IconTitle,
  TitleIcon,
}

export type TextButtonProps = {
  id?: string;
  type?: 'submit' | 'button';
  title?: string;
  color?: TextButtonColor;
  width?: TextButtonWidth;
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  style?: CSSProperties;
  testId?: string;
  order?: TextButtonOrder;

  tooltip?: React.ReactNode | string;
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
};

export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  function Button(props, ref) {
    const {
      type = 'button',
      title,
      color,
      width,
      className,
      disabled,
      icon,
      iconClassName,
      testId,
      tooltip,
      tooltipProps,
      order,
      ...rest
    } = {
      ...DefaultProps,
      ...props,
    };

    const button = (
      <button
        ref={ref}
        type={type}
        data-disabled={disabled}
        {...rest}
        className={clsx(
          className,
          styles.button,
          styles[`${width}-button`],
          styles[`${color}-button`],
        )}
        data-test-id={testId}
      >
        {order === TextButtonOrder.TitleIcon ? (
          <>
            {title}
            {icon}
          </>
        ) : (
          <>
            {icon}
            {title}
          </>
        )}
      </button>
    );

    if (!tooltip) return button;

    return (
      <BrightTooltip title={tooltip} {...tooltipProps}>
        <span className={className}>{button}</span>
      </BrightTooltip>
    );
  },
);

const DefaultProps: Partial<TextButtonProps> = {
  color: TextButtonColor.Default,
  width: TextButtonWidth.Content,
};
